import { Separator } from "@/components/ui/separator"
import { type ReactNode, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { type SidebarDefault, useLayoutShell } from "./LayoutShellContext"

interface LayoutHeaderProps {
  left?: ReactNode
  right?: ReactNode
  defaultSidebarState?: SidebarDefault
}

/**
 * A page's slice of the app shell: header content, portalled into the
 * shell's header so it stays in the page's own tree (and leaves with the
 * page), plus the sidebar state the page prefers, applied once per mount.
 */
export function LayoutHeader({
  left,
  right,
  defaultSidebarState,
}: LayoutHeaderProps) {
  const shell = useLayoutShell()
  const applied = useRef(false)

  useEffect(() => {
    if (!shell || !defaultSidebarState || applied.current) return
    applied.current = true
    shell.applySidebarDefault(defaultSidebarState)
  }, [shell, defaultSidebarState])

  if (!shell) return null

  return (
    <>
      {left && shell.leftSlot
        ? createPortal(
            <>
              <Separator
                orientation="vertical"
                className="mr-2 data-[orientation=vertical]:h-4"
              />
              <div className="flex-1">{left}</div>
            </>,
            shell.leftSlot,
          )
        : null}
      {right && shell.rightSlot ? createPortal(right, shell.rightSlot) : null}
    </>
  )
}
